import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { WorkspaceProvider } from '../context/WorkspaceContext';
import ChatSettings from '../components/admin/ChatSettings';

/**
 * Company admin chat widget configuration (standalone route, no dashboard shell).
 */
export default function ChatSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/', { replace: true });
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (!snap.exists()) {
          setDenied(true);
          setLoading(false);
          return;
        }
        const data = { ...snap.data(), uid: user.uid };
        const allowed =
          data.role === 'company_admin' || data.role === 'super_admin';
        if (!allowed || !data.companyId) {
          setDenied(true);
          setLoading(false);
          return;
        }
        setUserData(data);
      } catch (err) {
        console.error('ChatSettingsPage:', err);
        setDenied(true);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (denied || !userData?.companyId) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto', mt: 8 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Chat widget settings are available to company admins and super admins only.
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </Button>
      </Box>
    );
  }

  return (
    <WorkspaceProvider companyId={userData.companyId}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box sx={{ px: 3, pt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
            Back to dashboard
          </Button>
        </Box>
        <ChatSettings />
      </Box>
    </WorkspaceProvider>
  );
}
